import React from "react";
import { Loader2, MessageSquareText } from "lucide-react";
import BadgesComponent from "../tables/badges-component";
import "../tables/table.css";
import {
  FOLLOW_UP_STATUS,
  formatDisplayDate,
  formatQty,
  getCustomerName,
  getDueState,
} from "../../utils/follow-up";
import { DueBadge } from "./follow-up-badges";
import { CopyPhoneButton, StatusToggleButton, WhatsAppButton } from "./follow-up-actions";

const HEADINGS = [
  "Customer",
  "Phone",
  "Bill No.",
  "Item",
  "Qty",
  "Shortage",
  "Refill Date",
  "Due",
  "Status",
  "Comments",
  "Actions",
];

// Same cell styles as the other list tables (dynamic-transaction-table)
const TH_CLASS =
  "h-[50px] whitespace-nowrap border-b border-[var(--list-table-border-color)] px-[8px] text-left text-[var(--list-table-heading-fg-color)] text-[length:var(--list-table-heading-font-size)] font-[var(--list-table-heading-font-weight)]";
const TD_CLASS =
  "whitespace-nowrap border-b border-[var(--list-table-border-color)] px-[8px] text-left text-[length:var(--list-table-row-font-size)] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]";

// Clicks inside these cells act on their own controls, not the row
const stopRowClick = (event) => event.stopPropagation();

const MessageRow = ({ children }) => (
  <tr>
    <td colSpan={HEADINGS.length} className="h-[50px] px-[8px] text-center text-primary">
      {children}
    </td>
  </tr>
);

const FollowUpTableRow = ({ followUp, busyAction, onToggleStatus, onOpenDetails }) => {
  const phone = followUp.cmr_phone_number;
  const shortage = parseFloat(followUp.item_shortage_qty) || 0;
  const commentsCount = followUp.comments_count ?? 0;
  const isDone = followUp.rfu_status === FOLLOW_UP_STATUS.done;

  return (
    <tr
      onClick={() => onOpenDetails(followUp)}
      className="h-[50px] cursor-pointer bg-[var(--list-table-row-bg)] hover:bg-[var(--list-table-row-hover-bg)]"
    >
      <td className={`${TD_CLASS} font-[var(--follow-up-label-fw)]`}>{getCustomerName(followUp)}</td>
      <td className={TD_CLASS} onClick={stopRowClick}>
        {phone ? (
          <span className="flex items-center gap-1">
            <a href={`tel:${phone}`} className="text-[var(--follow-up-call-fg)] hover:underline">
              {phone}
            </a>
            <CopyPhoneButton phone={phone} />
          </span>
        ) : (
          "--"
        )}
      </td>
      <td className={TD_CLASS}>{followUp.sot_invoice_number ?? "--"}</td>
      <td className={`${TD_CLASS} max-w-[16rem] truncate`} title={followUp.item_name}>
        {followUp.item_name ?? "--"}
      </td>
      <td className={TD_CLASS}>{formatQty(followUp.item_quantity)}</td>
      <td className={`${TD_CLASS} ${shortage > 0 ? "text-[var(--follow-up-overdue-fg)]" : ""}`}>
        {shortage > 0 ? formatQty(shortage) : "--"}
      </td>
      <td className={TD_CLASS}>{formatDisplayDate(followUp.item_refill_date)}</td>
      <td className={TD_CLASS}>
        <DueBadge dueState={getDueState(followUp.item_refill_date, followUp.rfu_status)} />
      </td>
      <td className={TD_CLASS}>
        <BadgesComponent status={isDone ? "completed" : "pending"} />
      </td>
      <td className={TD_CLASS}>
        <span
          className="inline-flex items-center gap-1 text-[var(--follow-up-accent)]"
          title={followUp.last_comment ?? "No comments yet"}
        >
          <MessageSquareText size={14} aria-hidden="true" />
          {commentsCount}
        </span>
      </td>
      <td className={TD_CLASS} onClick={stopRowClick}>
        <div className="flex items-center gap-2">
          {/* opens the panel, where the items to send are picked */}
          <WhatsAppButton compact followUp={followUp} onClick={() => onOpenDetails(followUp)} />
          <StatusToggleButton
            compact
            followUp={followUp}
            isBusy={busyAction === "status"}
            onClick={onToggleStatus}
          />
        </div>
      </td>
    </tr>
  );
};

const FollowUpTable = ({
  followUps,
  isLoading,
  loadError,
  busyActions,
  onRetry,
  onToggleStatus,
  onOpenDetails,
}) => {
  const renderBody = () => {
    if (isLoading) {
      return (
        <MessageRow>
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            Loading follow-ups…
          </span>
        </MessageRow>
      );
    }
    if (loadError) {
      return (
        <MessageRow>
          Couldn't load the follow-ups. {loadError}{" "}
          <button type="button" onClick={onRetry} className="font-[var(--follow-up-label-fw)] underline">
            Try again
          </button>
        </MessageRow>
      );
    }
    if (followUps.length === 0) {
      return <MessageRow>No follow-ups found</MessageRow>;
    }
    return followUps.map((followUp) => (
      <FollowUpTableRow
        key={followUp.rfu_id}
        followUp={followUp}
        busyAction={busyActions[followUp.rfu_id]}
        onToggleStatus={onToggleStatus}
        onOpenDetails={onOpenDetails}
      />
    ));
  };

  return (
    <div className="tab-container min-h-0 flex-1 overflow-y-auto outline outline-[var(--list-table-border-color)] outline-1 border-radius">
      <table className="mt-0 w-full border-collapse">
        <thead className="sticky top-0 z-[10] bg-[var(--list-table-heading-bg)]">
          <tr>
            {HEADINGS.map((heading) => (
              <th key={heading} className={TH_CLASS}>
                {heading.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{renderBody()}</tbody>
      </table>
    </div>
  );
};

export default FollowUpTable;
